import Chart from 'chart.js/auto';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { displayPercentile } from '../../utils/percentileUtils';
import { resolveCaliforniaPlugInEvCount } from '../../domains/narrative/californiaEvFleet';
import type { EvChargingNarrativeSnapshot } from '../../domains/narrative/evChargingNarrative';

interface EVChargingInfrastructureProps {
  location: string;
  activeTab?: 'counts' | 'peoplePerCharger' | 'evsPerCharger';
  onTabChange?: (tab: 'counts' | 'peoplePerCharger' | 'evsPerCharger') => void;
  onNarrativeSnapshot?: (snapshot: EvChargingNarrativeSnapshot | null) => void;
}

interface ChargingData {
  county: string;
  total_chargers: number;
  l2_chargers: number;
  dc_fast: number;
  percentile?: number;
}

interface PerCapitaData {
  city: number;
  county: number;
  state: number;
}

interface EVData {
  city: number;
  county?: number;
  state: number | null;
}

const EVChargingInfrastructure: React.FC<EVChargingInfrastructureProps> = ({ 
  location, 
  activeTab = 'counts', 
  onTabChange,
  onNarrativeSnapshot,
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chargingData, setChargingData] = useState<ChargingData | null>(null);
  const [perCapitaData, setPerCapitaData] = useState<PerCapitaData | null>(null);
  const [evData, setEvData] = useState<EVData | null>(null);
  const [targetChargers2030, setTargetChargers2030] = useState<number | null>(null);


  // Fetch data once when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get the county for the location
        await supabaseLocationService.initialize();
        let county = supabaseLocationService.getCountyForCity(location);
        
        // If location is already a county, use it directly
        if (!county && location.toLowerCase().includes('county')) {
          county = location;
        }
        
        if (!county) {
          setError(`Could not determine county for ${location}`);
          setLoading(false);
          return;
        }

        // Get geofips for the location first
        let geofips = null;
        
        // Try to get geofips from population table
        const { data: popData } = await supabaseClient
          .from('population')
          .select('geofips, location_name, geography_type')
          .eq('location_name', location)
          .eq('year', 2023)
          .limit(1);
        
        if (popData && popData.length > 0) {
          geofips = popData[0].geofips;
        } else {
          // Fall back to county geofips
          const { data: countyPopData } = await supabaseClient
            .from('population')
            .select('geofips, location_name, geography_type')
            .eq('location_name', county)
            .eq('year', 2023)
            .limit(1);
          
          if (countyPopData && countyPopData.length > 0) {
            geofips = countyPopData[0].geofips;
          }
        }

        // Query ev_chargers by geofips
        // ev_chargers stores geofips without leading zeros (e.g. 618100); try both padded and unpadded
        let chargers, chargersError;
        
        if (geofips) {
          const str = String(geofips);
          const geofipsNum = parseInt(str.replace(/^0+/, '') || str, 10);
          const tryGeofips: (string | number)[] = [geofips];
          if (!Number.isNaN(geofipsNum) && geofipsNum > 0) tryGeofips.push(geofipsNum);

          chargers = null;
          chargersError = null;
          for (const gf of tryGeofips) {
            const { data: chargerData, error: chargerError } = await supabaseClient
              .from('ev_chargers')
              .select('location_name, county, l2_chargers, dc_fast, total_chargers, percentile, chargers_per_1000_residents_percentile, year')
              .eq('geofips', gf)
              .order('year', { ascending: false })
              .limit(1);
            chargers = chargerData;
            chargersError = chargerError;
            if (chargers && chargers.length > 0) {
              break;
            }
          }
        } else {
          chargersError = new Error('Could not determine geofips for location');
          chargers = null;
        }

        if (chargersError) {
          console.error('Error fetching chargers:', chargersError);
          setError(`Error loading EV chargers: ${chargersError.message}`);
          setLoading(false);
          return;
        }

        if (!chargers || chargers.length === 0) {
          setError(`No charging infrastructure data found for ${county}`);
          setLoading(false);
          return;
        }

        const row = chargers[0] as { percentile?: number; chargers_per_1000_residents_percentile?: number } & typeof chargers[0];
        const pct = row.percentile ?? row.chargers_per_1000_residents_percentile;
        const data: ChargingData = {
          county: county, // Use the actual county name
          total_chargers: row.total_chargers || 0,
          l2_chargers: row.l2_chargers || 0,
          dc_fast: row.dc_fast || 0,
          percentile: pct != null ? pct : undefined
        };

        setChargingData(data);

        // Get county charger data if location is a city
        let countyChargerData = null;
        if (county && county !== location) {
          try {
            const { data: countyPopData } = await supabaseClient
              .from('population')
              .select('geofips, location_name, geography_type')
              .eq('location_name', county)
              .eq('year', 2023)
              .limit(1);
            
            if (countyPopData && countyPopData.length > 0) {
              const countyGeofips = countyPopData[0].geofips;
              const cStr = String(countyGeofips);
              const cNum = parseInt(cStr.replace(/^0+/, '') || cStr, 10);
              const tryCountyGeofips: (string | number)[] = [countyGeofips];
              if (!Number.isNaN(cNum) && cNum > 0) tryCountyGeofips.push(cNum);

              let countyChargers: unknown[] | null = null;
              for (const gf of tryCountyGeofips) {
                const { data } = await supabaseClient
                  .from('ev_chargers')
                  .select('total_chargers, l2_chargers, dc_fast')
                  .eq('geofips', gf)
                  .limit(1);
                if (data && data.length > 0) {
                  countyChargers = data;
                  break;
                }
              }

              if (countyChargers && countyChargers.length > 0) {
                countyChargerData = countyChargers[0] as { total_chargers: number; l2_chargers: number; dc_fast: number };
              }
            }
          } catch (err) {
            console.error('Error fetching county charger data:', err);
          }
        }

        // Compute chargers per 1,000 residents for city, county, and California (2023)
        try {
          // City population - try multiple approaches
          let cityPop = 0;
          let cityPopRows = null;

          // First try: exact city match
          const { data: cityPopRows1 } = await supabaseClient
            .from('population')
            .select('population, location_name, year, geography_type')
            .eq('location_name', location)
            .eq('year', 2023)
            .eq('geography_type', 'city')
            .limit(1);

          if (cityPopRows1 && cityPopRows1.length > 0) {
            cityPop = cityPopRows1[0].population as number;
            cityPopRows = cityPopRows1;
          } else {
            // Second try: any geography type for this location
            const { data: cityPopRows2 } = await supabaseClient
              .from('population')
              .select('population, location_name, year, geography_type')
              .eq('location_name', location)
              .eq('year', 2023)
              .limit(1);
            
            if (cityPopRows2 && cityPopRows2.length > 0) {
              cityPop = cityPopRows2[0].population as number;
              cityPopRows = cityPopRows2;
            }
          }

          // If we still don't have city population, let's see what's available for Berkeley
          if (cityPop === 0) {
            await supabaseClient
              .from('population')
              .select('population, location_name, year, geography_type')
              .ilike('location_name', `%${location}%`)
              .limit(10);
          }

          // County population
          const { data: countyPopRows } = await supabaseClient
            .from('population')
            .select('population, location_name, year, geography_type')
            .eq('location_name', county)
            .eq('year', 2023)
            .limit(1);
          const countyPop = countyPopRows && countyPopRows.length > 0 ? (countyPopRows[0].population as number) : 0;

          // California population (geofips 6 or location_name 'California')
          const { data: caPopRows } = await supabaseClient
            .from('population')
            .select('population, geofips, year')
            .eq('geofips', 6)
            .eq('year', 2023)
            .limit(1);
          const caPopulation = caPopRows && caPopRows.length > 0 ? (caPopRows[0].population as number) : 0;

          // California total chargers - state row (geofips 6) or aggregate from counties
          const { data: caChargerData } = await supabaseClient
            .from('ev_chargers')
            .select('total_chargers, l2_chargers, dc_fast')
            .eq('geofips', 6) // California geofips
            .limit(1);
          let caTotalChargers = caChargerData && caChargerData.length > 0 ? (caChargerData[0].total_chargers ?? 0) : 0;
          if (caTotalChargers <= 0) {
            // No state-level row: sum county-level chargers (California counties geofips 6001–6115)
            const { data: caCountyChargers } = await supabaseClient
              .from('ev_chargers')
              .select('total_chargers')
              .gte('geofips', 6001)
              .lte('geofips', 6115);
            if (caCountyChargers && caCountyChargers.length > 0) {
              caTotalChargers = caCountyChargers.reduce((sum: number, r: { total_chargers?: number | null }) => sum + (Number(r.total_chargers) || 0), 0);
            }
          }

          // Calculate people per charger for city, county, and state
          const cityPeoplePerCharger = data.total_chargers > 0 ? cityPop / data.total_chargers : 0;
          const countyChargers = countyChargerData ? countyChargerData.total_chargers : data.total_chargers;
          const countyPeoplePerCharger = countyChargers > 0 ? countyPop / countyChargers : 0;
          const caPeoplePerCharger = caTotalChargers > 0 ? caPopulation / caTotalChargers : 0;

          // 2030 statewide target allocation: 1,000,000 chargers
          // Target is proportional to the entire state of California
          // Use city population for the target calculation, not county
          const target = caPopulation > 0 ? (cityPop / caPopulation) * 1_000_000 : 0;

          const peoplePerChargerSnapshot = {
            city: Math.round(cityPeoplePerCharger),
            county: Math.round(countyPeoplePerCharger),
            state: Math.round(caPeoplePerCharger),
          };
          setPerCapitaData(peoplePerChargerSnapshot);

          // Get EV data for EVs per charger calculation
          let evsPerChargerSnapshot: { city: number; county?: number; state: number | null } | undefined;
          let stateFleetAvailable = false;
          try {
            // Get city EV data - try emfac2025_fleet_mat first, fallback to emfac2021_fleet_mat
            let cityEVs = 0;
            const { data: city2025Data } = await supabaseClient
              .from('emfac2025_fleet_mat')
              .select('vehicle_population, fuel_type')
              .eq('geofips', geofips)
              .eq('geography_type', 'City')
              .eq('year', 2023)
              .in('fuel_type', ['Electric', 'PluginHybrid']);
            
            if (city2025Data) {
              cityEVs = city2025Data.reduce((sum: number, r: any) => sum + (r.vehicle_population || 0), 0);
            } else {
              const { data: city2021Data } = await supabaseClient
                .from('emfac2021_fleet_mat')
                .select('vehicle_population, fuel_type')
                .eq('geofips', geofips)
                .eq('geography_type', 'City')
                .eq('year', 2023)
                .in('fuel_type', ['Electric', 'PluginHybrid']);
              
              cityEVs = city2021Data ? city2021Data.reduce((sum: number, r: any) => sum + (r.vehicle_population || 0), 0) : 0;
            }

            // Get county EV data - try to get actual county data first
            let countyEVs = 0;
            if (countyChargerData) {
              // Get county geofips for EV data
              const { data: countyPopData } = await supabaseClient
                .from('population')
                .select('geofips')
                .eq('location_name', county)
                .eq('year', 2023)
                .limit(1);
              
              if (countyPopData && countyPopData.length > 0) {
                const countyGeofips = countyPopData[0].geofips;
                
                // Try emfac2025_fleet_mat first
                const { data: county2025Data } = await supabaseClient
                  .from('emfac2025_fleet_mat')
                  .select('vehicle_population, fuel_type')
                  .eq('geofips', countyGeofips)
                  .eq('geography_type', 'County')
                  .eq('year', 2023)
                  .in('fuel_type', ['Electric', 'PluginHybrid']);
                
                if (county2025Data) {
                  countyEVs = county2025Data.reduce((sum: number, r: any) => sum + (r.vehicle_population || 0), 0);
                } else {
                  // Fallback to emfac2021_fleet_mat
                  const { data: county2021Data } = await supabaseClient
                    .from('emfac2021_fleet_mat')
                    .select('vehicle_population, fuel_type')
                    .eq('geofips', countyGeofips)
                    .eq('geography_type', 'County')
                    .eq('year', 2023)
                    .in('fuel_type', ['Electric', 'PluginHybrid']);
                  
                  countyEVs = county2021Data ? county2021Data.reduce((sum: number, r: any) => sum + (r.vehicle_population || 0), 0) : 0;
                }
              }
            }
            
            const countyFleetObserved = countyEVs > 0;

            const caFleet = await resolveCaliforniaPlugInEvCount(supabaseClient, 2023);
            const caEVs = caFleet.count;
            stateFleetAvailable = caEVs != null && caEVs > 0;

            const cityEVsPerCharger = data.total_chargers > 0 ? cityEVs / data.total_chargers : 0;
            const countyEVsPerCharger =
              countyFleetObserved && countyChargers > 0 ? countyEVs / countyChargers : 0;
            const caEVsPerCharger =
              stateFleetAvailable && caTotalChargers > 0 && caEVs != null
                ? caEVs / caTotalChargers
                : null;

            evsPerChargerSnapshot = {
              city: Math.round(cityEVsPerCharger),
              ...(countyFleetObserved ? { county: Math.round(countyEVsPerCharger) } : {}),
              state: caEVsPerCharger != null ? Math.round(caEVsPerCharger) : null,
            };
            setEvData(evsPerChargerSnapshot);
          } catch (err) {
            console.error('Error fetching EV data:', err);
            setEvData(null);
          }

          setTargetChargers2030(target);

          onNarrativeSnapshot?.({
            locationName: location,
            countyName: county,
            viewId: activeTab,
            totalChargers: data.total_chargers,
            l2Chargers: data.l2_chargers,
            dcFastChargers: data.dc_fast,
            snapshotYear: (row as { year?: number }).year,
            peoplePerCharger: {
              ...peoplePerChargerSnapshot,
              state: peoplePerChargerSnapshot.state,
            },
            evsPerCharger: evsPerChargerSnapshot,
            target2030: target,
            percentile: data.percentile,
            stateFleetAvailable,
          });
        } catch (err) {
          console.error('Error computing per capita data:', err);
          setPerCapitaData(null);
          setTargetChargers2030(null);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error in EVChargingInfrastructure:', err);
        setError(`Error loading data: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setLoading(false);
      }
    };

    fetchData();
  }, [location]);

  // Create chart when data or tab changes
  const createChart = useCallback(() => {
    if (!chartRef.current || !chargingData) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    if (activeTab === 'counts') {
      // Always show the selected location name, not the data source
      const labels = [location, 'Illustrative CA 2030 benchmark'];
      
      chartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'Level 2 Chargers',
              data: [chargingData.l2_chargers, 0],
              backgroundColor: 'rgba(30, 58, 138, 0.8)',
              borderColor: 'rgba(30, 58, 138, 1)',
              borderWidth: 1,
              stack: 'actual'
            },
            {
              label: 'DC Fast Chargers',
              data: [chargingData.dc_fast, 0],
              backgroundColor: 'rgba(220, 38, 38, 0.8)',
              borderColor: 'rgba(220, 38, 38, 1)',
              borderWidth: 1,
              stack: 'actual'
            },
            {
              label: 'Population-scaled CA 2030 benchmark (illustrative)',
              data: [0, Math.max(0, targetChargers2030 || 0)],
              backgroundColor: 'rgba(245, 158, 11, 0.85)',
              borderColor: 'rgba(245, 158, 11, 1)',
              borderWidth: 1,
              stack: 'target'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            title: standardTitle(`EV Charging Infrastructure vs illustrative CA benchmark - ${location}`),
            legend: standardLegend,
            tooltip: {
              callbacks: {
                title: (context) => context[0].label,
                label: (context) => {
                  const datasetLabel = context.dataset.label;
                  const value = context.parsed.y;
                  if (datasetLabel === 'Level 2 Chargers') {
                    return [
                      `Level 2 Chargers: ${value}`,
                      `Total Chargers: ${chargingData.total_chargers}`,
                      chargingData.percentile != null ? `Percentile: ${displayPercentile(chargingData.percentile) ?? Math.round(chargingData.percentile)}th` : ''
                    ];
                  }
                  if (datasetLabel === 'DC Fast Chargers') {
                    return [
                      `DC Fast Chargers: ${value}`,
                      `Total Chargers: ${chargingData.total_chargers}`,
                      chargingData.percentile != null ? `Percentile: ${displayPercentile(chargingData.percentile) ?? Math.round(chargingData.percentile)}th` : ''
                    ];
                  }
                  return [
                    `Illustrative benchmark: ${Math.round(Math.max(0, targetChargers2030 || 0)).toLocaleString()} (place pop ÷ CA pop × 1M statewide projection; not an adopted local target)`,
                  ];
                }
              }
            }
          },
          scales: {
            x: { title: { display: true, text: '', font: { size: 14, weight: 'bold' } }, stacked: true },
            y: { title: { display: true, text: 'Number of Charging Stations', font: { size: 14, weight: 'bold' } }, beginAtZero: true, stacked: true }
          }
        }
      });
    } else if (activeTab === 'peoplePerCharger' && perCapitaData) {
      // Determine if location is a city or county
      const isCity = chargingData.county !== location;
      const labels = isCity ? [location, chargingData.county, 'California'] : [location, 'California'];
      const values = isCity ? [perCapitaData.city, perCapitaData.county, perCapitaData.state] : [perCapitaData.city, perCapitaData.state];
      chartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'People per Charger (2023)',
            data: values,
            backgroundColor: ['#2563eb', '#10b981', '#f59e0b']
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: { 
            legend: standardLegend,
            title: standardTitle(`People per Charger - ${location}`)
          },
          scales: { 
            y: { 
              beginAtZero: true, 
              title: { display: true, text: 'People per Charger' } 
            } 
          }
        }
      });
    } else if (activeTab === 'evsPerCharger' && evData) {
      const isCity = chargingData.county !== location;
      const labelParts: string[] = [location];
      const valueParts: number[] = [evData.city];
      if (isCity && evData.county != null) {
        labelParts.push(chargingData.county);
        valueParts.push(evData.county);
      }
      if (evData.state != null) {
        labelParts.push('California');
        valueParts.push(evData.state);
      }
      const labels = labelParts;
      const values = valueParts;
      chartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'EVs per Charger (2023)',
            data: values,
            backgroundColor: ['#2563eb', '#10b981', '#f59e0b']
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: { 
            legend: standardLegend,
            title: standardTitle(`EVs per Charger - ${location}`)
          },
          scales: { 
            y: { 
              beginAtZero: true, 
              title: { display: true, text: 'EVs per Charger' } 
            } 
          }
        }
      });
    }
  }, [activeTab, chargingData, location, perCapitaData, evData, targetChargers2030]);

  // Recreate chart when dependencies change
  useEffect(() => {
    if (!loading && !error) {
      createChart();
    }
  }, [createChart, loading, error]);

  if (loading) {
    return (
      <div className="text-center p-8">
        <div className="text-lg text-gray-600">Loading charging infrastructure data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-lg text-red-600">Error: {error}</div>
      </div>
    );
  }


  const chartContent = (
    <div style={{ height: 400, width: '100%', position: 'relative' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );


  return chartContent;
};

export default EVChargingInfrastructure;
